// src/components/buyer-leads/FollowUpsDueWidget.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../../utils/helper";
import { toast } from "react-toastify";
import { useStateValue } from "../../context/StateProvider";
import { actionType } from "../../context/reducer";
import { listenBuyerLeads, snoozeBuyerLead, dismissBuyerFollowUp } from "../../utils/firebasefunctions";
import {
  isFollowUpDue,
  sortByFollowUp,
  getRatingColor,
  followUpStatus,
  newActivity,
  addDaysIso,
} from "../../utils/buyerActivity";
import LogActivityModal from "./LogActivityModal";

// Local-time today as ISO "YYYY-MM-DD" (matches the date-input format).
const todayIso = () => {
  const d = new Date();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${d.getFullYear()}-${m}-${day}`;
};

export default function FollowUpsDueWidget() {
  const [{ user, buyerLeads }, dispatch] = useStateValue();
  const [logLead, setLogLead] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    const unsub = listenBuyerLeads((rows) =>
      dispatch({ type: actionType.STORE_BUYER_LEADS, payload: rows }),
    );
    return () => unsub && unsub();
  }, [dispatch]);

  // Show only the viewer's OWN due follow-ups — leads assigned to or created by
  // them. Team Leads / Client Care no longer see the whole team's follow-ups
  // here: this widget is a personal to-do list, not a team roster.
  const due = useMemo(() => {
    const t = todayIso();
    const mine = (buyerLeads || []).filter((l) => {
      if (l.status !== "active") return false;
      if (!isFollowUpDue(l, t)) return false;
      return l.createdby === user?.id || l.assignedAgent === user?.id;
    });
    return sortByFollowUp(mine);
  }, [buyerLeads, user?.id]);

  const lastNote = (lead) => {
    const acts = lead.activities || [];
    if (!acts.length) return "";
    const latest = [...acts].sort((a, b) => (b.at || 0) - (a.at || 0))[0];
    return latest?.note || latest?.type || "";
  };

  // Snooze: push this lead's next follow-up forward and log a note to its
  // timeline (no lastContacted change — a snooze isn't a contact). The
  // buyerLeads listener refreshes `due`, so the lead drops off this list.
  const handleSnooze = async (lead, newDate) => {
    if (!newDate) return;
    const at = Date.now();
    const activity = newActivity({
      type: "Note",
      note: `Snoozed follow-up to ${newDate}`,
      by: user?.id || "",
      byName:
        [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
        user?.name ||
        "",
      id: `${at}`,
      at,
    });
    const res = await snoozeBuyerLead(lead.id, activity, newDate);
    if (res.success) toast.success(`Snoozed to ${newDate}`);
    else toast.error(res.error || "Couldn't snooze");
  };

  // Dismiss: no new date, no contact — just stop showing this follow-up.
  // Leaves a note on the lead so the decision is visible in its timeline.
  const handleDismiss = async (lead) => {
    const at = Date.now();
    const activity = newActivity({
      type: "Note",
      note: "Dismissed follow-up",
      by: user?.id || "",
      byName:
        [user?.firstname, user?.lastname].filter(Boolean).join(" ") ||
        user?.name ||
        "",
      id: `${at}`,
      at,
    });
    const res = await dismissBuyerFollowUp(lead.id, activity);
    if (res.success) toast.success("Follow-up dismissed");
    else toast.error(res.error || "Couldn't dismiss");
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm h-full">
      <div className="px-5 py-4 border-b border-slate-100 flex items-center gap-2">
        <span className="text-base font-semibold text-slate-900">Follow-Ups Due</span>
        <span className="text-xs bg-slate-100 text-slate-600 rounded-full px-2 py-0.5">{due.length}</span>
        <span className="text-xs text-slate-400 ml-1">today &amp; overdue buyer leads</span>
      </div>
      {due.length === 0 ? (
        <div className="px-5 py-8 text-center text-sm text-slate-500">You're all caught up — no follow-ups due.</div>
      ) : (
        <ul>
          {due.map((lead) => {
            const overdue = followUpStatus(lead, todayIso()) === "overdue";
            return (
              <li key={lead.id} className="px-5 py-3 border-b border-slate-50 last:border-b-0 flex items-center gap-3">
                {lead.rating && (
                  <span className={`text-[10px] font-bold px-1.5 rounded ${getRatingColor(lead.rating)}`}>{lead.rating}</span>
                )}
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-slate-800 truncate">{lead.contactName || "(unnamed lead)"}</div>
                  <div className="text-[11px] truncate text-slate-500">
                    <span className={overdue ? "text-red-600 font-medium" : ""}>{overdue ? "Overdue" : "Today"} · {formatDate(lead.nextFollowUp)}</span>
                    {lastNote(lead) ? ` · ${lastNote(lead)}` : ""}
                  </div>
                </div>
                <button onClick={() => setLogLead(lead)} className="text-xs font-semibold bg-[#1E293B] text-white rounded-lg px-3 py-1.5">Log</button>
                <SnoozeMenu onPick={(d) => handleSnooze(lead, d)} />
                <button onClick={() => navigate("/dashboard/buyers")} className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5">Open</button>
                <button onClick={() => handleDismiss(lead)} title="Dismiss this follow-up" className="text-xs font-semibold border border-slate-200 text-slate-400 hover:text-slate-600 hover:border-slate-300 rounded-lg px-3 py-1.5">Dismiss</button>
              </li>
            );
          })}
        </ul>
      )}
      {logLead && (
        <LogActivityModal lead={logLead} onClose={() => setLogLead(null)} onLogged={() => setLogLead(null)} />
      )}
    </div>
  );
}

// Snooze button + dropdown: quick presets (Tomorrow / 3 days / 1 week) plus a
// date picker. Calls onPick(isoDate) with the chosen next-follow-up date.
function SnoozeMenu({ onPick }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const pick = (d) => {
    setOpen(false);
    onPick(d);
  };

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        className="text-xs font-semibold border border-slate-300 text-slate-600 rounded-lg px-3 py-1.5 hover:bg-slate-50"
      >
        Snooze
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-20 w-44 bg-white border border-slate-200 rounded-lg shadow-lg py-1 text-sm">
          <button
            className="w-full text-left px-3 py-2 hover:bg-slate-50"
            onClick={() => pick(addDaysIso(1))}
          >
            Tomorrow
          </button>
          <button
            className="w-full text-left px-3 py-2 hover:bg-slate-50"
            onClick={() => pick(addDaysIso(3))}
          >
            In 3 days
          </button>
          <button
            className="w-full text-left px-3 py-2 hover:bg-slate-50"
            onClick={() => pick(addDaysIso(7))}
          >
            In 1 week
          </button>
          <div className="border-t border-slate-100 mt-1 pt-1 px-3 pb-1">
            <label className="block text-[11px] text-slate-400 mb-1">
              Pick a date
            </label>
            <input
              type="date"
              className="w-full text-sm border border-slate-300 rounded px-2 py-1.5 outline-none focus:border-[#1E293B]"
              onChange={(e) => e.target.value && pick(e.target.value)}
            />
          </div>
        </div>
      )}
    </div>
  );
}
